import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Event, NavigationEnd, Router } from '@angular/router';

import { combineLatest, interval, Observable, ReplaySubject, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, shareReplay, startWith, take } from 'rxjs/operators';

import { CommonModule } from '@angular/common';

import { NavigationLinkDirective } from '../../../directives/navigation-link/navigation-link.directive';
import { ShBadgeComponent } from '../../badge/badge.component';
import { ShIconModule } from '../../icon/icon.module';
import { LinkItem, OpenableNavigationItem } from '../../navigation-item/entities/navigation-item.entity';

@Component({
    standalone: true,
    selector: 'sh-compact-navigation-item',
    templateUrl: 'compact-navigation-item.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, ShIconModule, ShBadgeComponent, NavigationLinkDirective]
})
export class ShCompactNavigationItemComponent implements OnInit {
    private readonly CLOSE_DELAY_IN_MILISECONDS = 200;

    private currentLocation$!: Observable<string>;
    isActive$!: Observable<boolean>;
    item$: Subject<OpenableNavigationItem> = new ReplaySubject(1);

    @Input() set item(item: OpenableNavigationItem) {
        this.item$.next(item);
    }

    @Input() openDropmenuAbove = false;

    @Output() selected: EventEmitter<boolean> = new EventEmitter();

    constructor(private readonly router: Router) {}

    ngOnInit(): void {
        this.initStateStreams();
    }

    private initStateStreams(): void {
        this.currentLocation$ = this.router.events.pipe(
            filter((event: Event): event is NavigationEnd => event instanceof NavigationEnd),
            map((event: NavigationEnd) => event.urlAfterRedirects),
            startWith(this.router.url),
            distinctUntilChanged(),
            shareReplay()
        );

        this.isActive$ = combineLatest([this.currentLocation$, this.item$]).pipe(
            map(([url, item]) => this.router.isActive(item.navigationLink?.route, false)),
            shareReplay()
        );
    }

    hasDropMenuItems(item: OpenableNavigationItem | null): boolean {
        if (item === null) {
            return false;
        }
        return 'dropmenuItems' in item;
    }

    openSubMenu(): void {
        this.selected.emit(true);
    }

    closeSubMenu(): void {
        interval(this.CLOSE_DELAY_IN_MILISECONDS)
            .pipe(take(1))
            .subscribe(() => {
                this.selected.emit(false);
            });
    }

    public onHover(event: MouseEvent, item: LinkItem) {
        if (item.onHover) {
            item.onHover(event);
        }
    }
}
